import React from 'react';
import { IntlShape } from 'react-intl';
import { createRoutingURI, getRootSlug } from '@scripts/utils/domain';
import {
  Event,
  Publication,
  TeaserCard,
  TeaserStorys,
  TeaserTools,
} from 'ech-components/src/components';
import { TeaserFunding } from '~/components';
import { ImageInterface } from 'ech-components/src/types';
import publicationService, {
  PublicationQueryProps,
} from './publicationService';
import { EventProps } from 'ech-components/src/components/Event';
import { getRootTranslation } from '../../gatsby-helper';

const imageSizes = {
  0: [462, 364],
  480: [770, 364],
  768: [505, 364],
  1024: [616, 364],
  1280: [565, 364],
  1440: [426, 364],
};

const genericTeaserModuleService = (
  {
    teaserTitle,
    leadText,
    toTeaser,
    displayStyle = 'Generic',
    displayAsGrid = false,
  }: GenericTeaserModuleServiceProps,
  intl: IntlShape,
) => {
  if (!toTeaser.length) return null;

  const items = toTeaser
    .map((item: GenericTeaserModuleServiceItemProps) => {
      let content = null;

      switch (item.__typename) {
        case 'ContentfulLinkTeaserElement': {
          const targetLink = item.linkTarget;
          const linkType = targetLink.__typename;
          let href = '';
          let linkTitle = '';
          let linkTarget = '_self';
          let linkIconBefore;
          let linkIconAfter;

          switch (linkType) {
            case 'ContentfulExternalLink':
              href = targetLink.url;
              linkTitle = targetLink?.linkText;
              linkTarget = '_blank';
              linkIconBefore = null;
              linkIconAfter = 'AnimatedIconArrowUpRight';
              break;
            case 'ContentfulMailLink':
              href = `mailto:${targetLink?.emailAddress}`;
              linkTitle = targetLink.text;
              break;
            default:
              break;
          }

          const data = {
            title: item.teaserTitle,
            teaser: item.teaserText,
            href,
            linkTitle,
            linkText: item.linkText || linkTitle,
            linkTarget,
            linkIconBefore,
            linkIconAfter,
          };

          content = <TeaserCard data={data} />;
          break;
        }
        case 'ContentfulPublication': {
          const data = publicationService(item);

          content = data ? <Publication data={data} /> : null;
          break;
        }
        case 'ContentfulStoryHub': {
          const data = {
            title: item.title,
            href: createRoutingURI(getRootSlug(item, intl.locale)),
            teaserImage: {
              ...item.teaserImage,
              imageSizes,
            },
            node_locale: intl.locale,
            belongsToCampaign: true,
          };

          content = <TeaserStorys data={data} />;
          break;
        }
        case 'ContentfulToolHub': {
          const data = {
            title: item.title,
            teaser: item.teaser,
            moreLinkText: intl.formatMessage({
              id: `hub.teaser.more.tool`,
            }),
            href: createRoutingURI(getRootSlug(item, intl.locale)),
          };

          content = <TeaserTools data={data} />;
          break;
        }
        case 'ContentfulNewsHub': {
          const data = {
            date: item.date,
            title: item.title,
            teaser: item.teaser,
            linkText: intl.formatMessage({
              id: 'hub.teaser.more.news',
            }),
            href: createRoutingURI(getRootSlug(item, intl.locale)),
          };

          content = <TeaserCard data={data} variant="news" />;
          break;
        }
        case 'ContentfulEventHub': {
          const data = {
            __typename: item.__typename,
            node_locale: item.node_locale,
            provider: item.provider,
            title: item.title,
            location: item.location,
            plz: item.plz,
            eventDate: item.eventDate,
            href: createRoutingURI(getRootSlug(item, intl.locale)),
          };

          content = <Event data={data} />;
          break;
        }
        case 'ContentfulDefaultHub': {
          const data = {
            title: item.title,
            teaser: item.teaser,
            teaserImage: {
              ...item.teaserImage,
              imageSizes,
            },
            linkTitle: item.navigationTitle,
            linkText: intl.formatMessage({
              id: 'hub.teaser.more.campaign',
            }),
            href: createRoutingURI(getRootSlug(item, intl.locale)),
          };

          content = <TeaserCard data={data} variant="backgroundImage" />;
          break;
        }
        case 'ContentfulFundingHub': {
          const data = {
            title: item.title,
            teaser: item.teaser,
            moreLinkText: intl.formatMessage({
              id: `hub.teaser.more.fundings`,
            }),
            href: createRoutingURI(getRootSlug(item, intl.locale)),
          };

          content = <TeaserFunding data={data} />;
          break;
        }
        case 'ContentfulConsultationHub': {
          const data = {
            title: item.title,
            teaser: item.teaser,
            teaserImage: {
              ...item.teaserImage,
              imageSizes,
            },
            linkTitle: item.navigationTitle,
            linkText: intl.formatMessage({
              id: 'hub.teaser.more.campaign',
            }),
            href: createRoutingURI(getRootSlug(item, intl.locale)),
          };

          content = <TeaserCard data={data} variant="backgroundImage" />;
          break;
        }
        default:
          break;
      }

      return content
        ? {
            type: item.__typename,
            id: item.contentful_id,
            content,
          }
        : null;
    })
    .filter((el) => el);

  let showMoreLink;

  switch (displayStyle) {
    case 'Stories': {
      showMoreLink = {
        text: intl.formatMessage({ id: 'hub.teaser.showmore.stories' }),
        title: intl.formatMessage({ id: 'page.navigation.stories' }),
        href: createRoutingURI({
          root: getRootTranslation('stories', intl.locale),
          slug: '',
          locale: intl.locale,
        }),
      };
      break;
    }
    case 'Tools': {
      showMoreLink = {
        text: intl.formatMessage({ id: 'hub.teaser.showmore.tools' }),
        title: intl.formatMessage({ id: 'page.navigation.tools' }),
        href: createRoutingURI({
          root: getRootTranslation('tools', intl.locale),
          slug: '',
          locale: intl.locale,
        }),
      };
      break;
    }
    case 'News': {
      showMoreLink = {
        text: intl.formatMessage({ id: 'hub.teaser.showmore.news' }),
        title: intl.formatMessage({ id: 'page.navigation.news' }),
        href: createRoutingURI({
          root: 'news',
          slug: '',
          locale: intl.locale,
        }),
      };
      break;
    }
    case 'Events': {
      showMoreLink = {
        text: intl.formatMessage({ id: 'hub.teaser.showmore.events' }),
        title: intl.formatMessage({ id: 'page.navigation.events' }),
        href: createRoutingURI({
          root: getRootTranslation('events', intl.locale),
          slug: '',
          locale: intl.locale,
        }),
      };
      break;
    }
    case 'Fundings': {
      showMoreLink = {
        text: intl.formatMessage({ id: 'hub.teaser.showmore.fundings' }),
        title: intl.formatMessage({ id: 'page.navigation.fundings' }),
        href: createRoutingURI({
          root: getRootTranslation('fundings', intl.locale),
          slug: '',
          locale: intl.locale,
        }),
      };
      break;
    }
  }

  return {
    title: teaserTitle,
    lead: leadText?.leadText,
    items,
    displayStyle,
    displayAsGrid,
    showMoreLink,
  };
};

export type GenericTeaserModuleServiceItemProps = {
  __typename: string;
  contentful_id: string;
  node_locale: string;
  title: string;
  date?: string;
  teaser?: string;
  teaserTitle: string;
  teaserText?: string;
  teaserImage: ImageInterface['image'];
  linkText?: string;
  navigationTitle: string;
  linkTarget: {
    __typename: string;
    text: string;
    linkText: string;
    url: string;
    emailAddress?: string;
  };
} & PublicationQueryProps &
  EventProps['data'];

export type GenericTeaserModuleServiceProps = {
  teaserTitle: string;
  leadText?: {
    leadText: string;
  };
  toTeaser: GenericTeaserModuleServiceItemProps[];
  displayStyle?: string;
  displayAsGrid?: boolean;
};

export default genericTeaserModuleService;
